<template>
  <div class="comment-thread">
    <h3 class="comment-header">Comments</h3>
    
    <div v-if="loading" class="loading">Loading comments...</div>
    <div v-else-if="error" class="error">{{ error }}</div>
    
    <div v-else class="comments-container">
      <!-- Display top-level comments -->
      <div v-for="comment in topLevelComments" :key="comment.CommentId" class="comment-item">
        <CommentItem 
          :comment="comment"
          :replies="getReplies(comment.CommentId)"
          :current-username="currentUsername"
          @reply="(commentId, replyText) => handleReply(commentId, replyText)"
          @edit="(commentId, newText) => handleEdit(commentId, newText)"
        />
      </div>
      
      <!-- No comments message -->
      <div v-if="topLevelComments.length === 0" class="no-comments">
        No comments yet.
      </div>
    </div>
    
    <!-- New comment form -->
    <div class="new-comment-form">
      <textarea
        v-model="newCommentText"
        placeholder="Add a comment..."
        class="comment-input"
        rows="3"
      ></textarea>
      <button 
        @click="submitComment()" 
        :disabled="!newCommentText.trim() || submitting"
        class="submit-button"
      >
        {{ submitting ? 'Posting...' : 'Post Comment' }}
      </button>
    </div>
  </div>
</template>

<script>
import ACTApiService from '@/services/ACTApiService.js';
import CommentItem from './CommentItem.vue';

export default {
  name: 'CommentThread',
  components: {
    CommentItem
  },
  props: {
    visitId: {
      type: String,
      required: true
    },
    taskType: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      comments: [],
      loading: false,
      error: null,
      newCommentText: '',
      submitting: false,
      replyingTo: null,
      editingComment: null,
      currentUsername: null
    };
  },
  created() {
    ACTApiService.instance = null;
    this.actApiService = new ACTApiService();
  },
  computed: {
    compositeLogId() {
      return `${this.visitId}|TASK|${this.taskType}`;
    },
    // Comments that are "overridden" (replaced by another) - hide from display
    overriddenCommentIds() {
      const overridden = new Set();
      this.comments.forEach(c => {
        if (c.OverrideId) overridden.add(c.OverrideId);
      });
      return overridden;
    },
    topLevelComments() {
      return this.comments
        .filter(c => !c.ParentCommentId && !this.overriddenCommentIds.has(c.CommentId))
        .sort((a, b) => new Date(a.DateTimeCreated) - new Date(b.DateTimeCreated));
    }
  },
  async mounted() {
    const userData = await this.getUserData();
    this.currentUsername = userData?.username || null;
    await this.loadComments();
  },
  methods: {
    async loadComments() {
      if (!this.visitId || !this.taskType) {
        this.error = 'Missing visit ID or task type';
        this.loading = false;
        return;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        if (!this.actApiService) {
          throw new Error('ACTApiService not initialized');
        }
        if (typeof this.actApiService.getCommentsForLog !== 'function') {
          throw new Error('getCommentsForLog is not a function. Available methods: ' + Object.keys(this.actApiService));
        }
        this.comments = await this.actApiService.getCommentsForLog(this.compositeLogId);
      } catch (err) {
        this.error = 'Failed to load comments: ' + err.message;
        this.comments = []; // Show empty state
      } finally {
        this.loading = false;
      }
    },
    
    getReplies(parentCommentId) {
      return this.comments
        .filter(c => c.ParentCommentId === parentCommentId && !this.overriddenCommentIds.has(c.CommentId))
        .sort((a, b) => new Date(a.DateTimeCreated) - new Date(b.DateTimeCreated));
    },
    
    async submitComment(parentCommentId = null) {
      if (!this.newCommentText.trim()) return;
      
      this.submitting = true;
      
      try {
        if (typeof this.actApiService.createComment !== 'function') {
          throw new Error('createComment method not found on ACTApiService');
        }
        
        const userData = await this.getUserData();
        await this.actApiService.createComment(
          this.compositeLogId,
          parentCommentId,
          null, // no override - new comment
          this.newCommentText.trim(),
          JSON.stringify(userData)
        );
        
        this.newCommentText = '';
        
        try {
          await this.loadComments();
        } catch (loadError) {
          console.warn('Comment was saved successfully, but failed to reload comments:', loadError);
          // Silently retry once
          try {
            await this.loadComments();
          } catch (retryError) {
            console.warn('Retry also failed, but comment was saved:', retryError);
          }
        }
      } catch (err) {
        console.error('Error submitting comment:', err);
        alert('Failed to post comment: ' + err.message);
      } finally {
        this.submitting = false;
      }
    },
    
    async handleReply(commentId, replyText) {
      if (!replyText || !replyText.trim()) return;
      
      this.submitting = true;
      
      try {
        const userData = await this.getUserData();
        await this.actApiService.createComment(
          this.compositeLogId,
          commentId, // parent comment ID
          null, // no override - new reply
          replyText.trim(),
          JSON.stringify(userData)
        );
        
        await this.loadComments();
      } catch (err) {
        console.error('Error submitting reply:', err);
        alert('Failed to post reply. Please try again.');
      } finally {
        this.submitting = false;
      }
    },
    
    async handleEdit(commentId, newText) {
      this.submitting = true;
      try {
        const comment = this.comments.find(c => c.CommentId === commentId);
        if (!comment) return;

        const userData = await this.getUserData();
        // Immutable: create new comment with overrideId = commentId (replaces the old one)
        await this.actApiService.createComment(
          this.compositeLogId,
          comment.ParentCommentId,
          commentId, // overrideId - this new comment replaces the old one
          newText,
          JSON.stringify(userData)
        );
        await this.loadComments();
      } catch (err) {
        console.error('Error editing comment:', err);
        alert('Failed to save edit. Please try again.');
      } finally {
        this.submitting = false;
      }
    },
    
    async getUserData() {
      try {
        // Try to get user info from Cognito token
        const { CognitoUserPool } = await import('amazon-cognito-identity-js');
        
        const poolData = {
          UserPoolId: 'us-east-2_3novul0pN',
          ClientId: '51v76uda8o5l6niokhg852qnj5'
        };
        
        const userPool = new CognitoUserPool(poolData);
        const cognitoUser = userPool.getCurrentUser();
        
        if (cognitoUser) {
          return new Promise((resolve) => {
            cognitoUser.getSession((err, session) => {
              if (!err && session && session.isValid()) {
                const idToken = session.getIdToken();
                const payload = idToken.payload;
                
                resolve({
                  username: payload['cognito:username'] || payload.username || 'Unknown User',
                  email: payload.email || '',
                  timestamp: new Date().toISOString()
                });
              } else {
                resolve({
                  username: 'Unknown User',
                  email: '',
                  timestamp: new Date().toISOString()
                });
              }
            });
          });
        }
      } catch (error) {
        console.error('Error getting user data:', error);
      }
      
      // Fallback
      return {
        username: 'Unknown User',
        email: '',
        timestamp: new Date().toISOString()
      };
    }
  },
  watch: {
    visitId: {
      handler() {
        if (this.visitId && this.taskType) {
          this.loadComments();
        }
      },
      immediate: false
    },
    taskType: {
      handler() {
        if (this.visitId && this.taskType) {
          this.loadComments();
        }
      },
      immediate: false
    }
  }
};
</script>

<style scoped>
.comment-thread {
  padding: 24px 0;
  border-top: 1px solid #e5e7eb;
  margin-top: 24px;
}

.comment-header {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #111827;
}

.loading, .error {
  padding: 10px;
  text-align: center;
  color: #666;
}

.error {
  color: #d9534f;
}

.comments-container {
  margin-bottom: 24px;
}

.no-comments {
  padding: 24px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background-color: #f9fafb;
  border-radius: 8px;
}

.new-comment-form {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.comment-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-bottom: 12px;
  background-color: #fff;
}

.comment-input:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.2);
}

.submit-button {
  background-color: #4a90e2;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
}

.submit-button:hover:not(:disabled) {
  background-color: #357abd;
}

.submit-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.no-log-id-message {
  padding: 20px;
  text-align: center;
  color: #666;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin-top: 20px;
}

.no-log-id-message p {
  margin: 0;
  font-size: 14px;
}
</style>
